import React, { useMemo } from "react";
import styled from "styled-components";

const hueFromString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
};

const Card = styled.div`
  width: 100%;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: transform 0.3s ease, border-color 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(139, 124, 255, 0.35);
  }

  @media (max-width: 640px) {
    padding: 18px 18px;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const Badge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Space Grotesk", sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.92);
  background: ${({ $from, $to }) => `linear-gradient(135deg, ${$from}, ${$to})`};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Role = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`;

const CompanyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13.5px;
  color: ${({ theme }) => theme.text_secondary};
`;

const Company = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.secondary};
`;

const Sep = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.text_muted};
`;

const Description = styled.p`
  font-size: 14.5px;
  line-height: 1.75;
  color: ${({ theme }) => theme.text_secondary};
`;

const SkillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 2px;
`;

const SkillTag = styled.span`
  font-size: 12px;
  font-weight: 500;
  padding: 5px 11px;
  border-radius: 100px;
  color: ${({ theme }) => theme.primary};
  background: rgba(139, 124, 255, 0.1);
  border: 1px solid rgba(139, 124, 255, 0.18);
`;

const ExperienceCard = ({ experience }) => {
  const hue = useMemo(() => hueFromString(experience.company), [experience.company]);
  const from = `hsl(${hue}, 65%, 46%)`;
  const to = `hsl(${(hue + 50) % 360}, 70%, 36%)`;
  const initials = experience.company
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <Card>
      <Top>
        <Badge $from={from} $to={to}>
          {initials}
        </Badge>
        <Body>
          <Role>{experience.role}</Role>
          <CompanyRow>
            <Company>{experience.company}</Company>
            <Sep />
            <span>{experience.date}</span>
          </CompanyRow>
        </Body>
      </Top>
      <Description>{experience.desc}</Description>
      {experience.skills && (
        <SkillRow>
          {experience.skills.map((skill) => (
            <SkillTag key={skill}>{skill}</SkillTag>
          ))}
        </SkillRow>
      )}
    </Card>
  );
};

export default ExperienceCard;
